import { autoUpdate, flip, shift, useFloating, UseFloatingOptions } from "@floating-ui/react";
import classNames from "classnames";
import FocusTrap from "focus-trap-react";
import compact from "lodash/compact";
import { RangeCalendar } from "PFComponents/calendar/range_calendar";
import { InputFieldSet, InputFieldSetProps } from "PFComponents/text/input_field_set";
import { isActionKeyPressed } from "PFCore/helpers/is_action_key_pressed";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import i18n from "PFCore/i18n";
import { IconName } from "PFTheme/graphics/icons";
import { EventKey } from "PFTypes/event_key";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { CalendarProps } from "../calendar";
import { Option } from "../range_calendar/options";
import css from "./inline_calendar.module.scss";

type InlineRangeCalendarProps = Pick<
  InputFieldSetProps,
  | "label"
  | "name"
  | "disabled"
  | "qaId"
  | "error"
  | "warning"
  | "required"
  | "lockedTip"
  | "locked"
  | "variant"
  | "tabIndex"
> & {
  startDate: string;
  endDate: string;
  handleChange: (startDate: string | null, endDate: string | null, period: string | null) => void;
  options?: Option[];
  onStartDateChange: (startDate: string) => void;
  portalRef?: RefObject<HTMLDivElement>;
  floatingOptions?: Partial<UseFloatingOptions>;
  style?: React.CSSProperties;
  letClear?: boolean;
  onRestore?: () => void;
  className?: string;
  dialogClassName?: string;
  inputFieldSetProps?: InputFieldSetProps;
  selectedPeriod?: string;
  maxDaysLength?: number;
  inputRef?: RefObject<HTMLInputElement>;
} & CalendarProps;

export const InlineRangeCalendar = ({
  label,
  name,
  error,
  warning,
  disabled,
  style,
  qaId,
  startDate,
  endDate,
  handleChange,
  options,
  letClear,
  onRestore,
  className,
  dialogClassName,
  inputFieldSetProps = {},
  selectedPeriod,
  onStartDateChange,
  portalRef,
  required,
  floatingOptions,
  locked,
  lockedTip,
  variant,
  tabIndex,
  inputRef,
  ...calendarProps
}: InlineRangeCalendarProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const { formatDate } = useDateFormatter();

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleShowCalendar = () => {
    setShowCalendar(true);
  };

  const handleCalendarChange = (startDate, endDate, period) => {
    setShowCalendar(false);
    handleChange(startDate, endDate, period);
  };

  const handleCalendarKeyDown = (event) => {
    if (event.key === EventKey.Escape) {
      handleHideCalendar();
    }
  };

  const handleInputKeyDown = (event) => {
    if (isActionKeyPressed(event)) {
      handleShowCalendar();
    } else if (event.key === EventKey.Escape) {
      handleHideCalendar();
    }
  };

  const handleHideCalendar = () => {
    setShowCalendar(false);
  };

  const handleFocusOut = (event) => {
    if (event.relatedTarget && !calendarRef.current?.contains(event.relatedTarget)) {
      handleHideCalendar();
    }
  };

  useEffect(() => {
    if (showCalendar) {
      window.addEventListener("mousedown", handleHideCalendar);
      calendarRef.current?.addEventListener("focusout", handleFocusOut);
    } else {
      window.removeEventListener("mousedown", handleHideCalendar);
      calendarRef.current?.removeEventListener("focusout", handleFocusOut);
    }
    return () => {
      window.removeEventListener("mousedown", handleHideCalendar);
      calendarRef.current?.removeEventListener("focusout", handleFocusOut);
    };
  }, [showCalendar]);

  const value = useMemo(() => {
    if (selectedPeriod && options) {
      const periodOption = options.find(({ id }) => id === selectedPeriod);
      return periodOption?.displayElement;
    }
    return startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : "";
  }, [startDate, endDate, selectedPeriod, options, formatDate]);

  const { refs, floatingStyles } = useFloating({
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [
      flip(),
      shift({
        mainAxis: true,
        crossAxis: true
      })
    ],
    whileElementsMounted: autoUpdate,
    ...floatingOptions
  });

  const CalendarDropdown = (
    <FocusTrap>
      <div
        role="dialog"
        ref={refs.setFloating}
        className={classNames(css.dialog, dialogClassName)}
        style={floatingStyles}
      >
        <div
          role="menu"
          tabIndex={-1}
          className={css.inlineRoot}
          ref={calendarRef}
          onMouseDown={stopPropagation}
        >
          <RangeCalendar
            {...calendarProps}
            portalRef={portalRef}
            startDate={startDate}
            endDate={endDate}
            handleChange={handleCalendarChange}
            options={options}
            onStartDateChange={onStartDateChange}
            onKeyDown={handleCalendarKeyDown}
          />
        </div>
      </div>
    </FocusTrap>
  );

  const actionIcons = useMemo(
    () =>
      compact([
        onRestore && {
          name: "history" as IconName,
          onClick: onRestore,
          title: label
            ? i18n.t("core:components.autoselect.restoreValue", { label })
            : i18n.t("core:components.autoselect.restore")
        },
        value &&
          letClear && {
            name: "filter-clean" as IconName,
            onClick: (event) => {
              event.preventDefault();
              handleChange(null, null, null);
            },
            title: label
              ? i18n.t("core:components.autoselect.clearValue", { label })
              : i18n.t("core:components.autoselect.clear")
          }
      ]),
    [handleChange, onRestore, value, letClear, label]
  );

  return (
    <div
      className={classNames(css.root, className, { [css.tableVariant]: variant === "table" })}
      style={style}
      ref={refs.setReference}
    >
      <InputFieldSet
        ref={inputRef}
        aria-expanded={showCalendar}
        aria-haspopup="dialog"
        icon="calendar"
        label={label}
        name={name}
        disabled={disabled}
        locked={locked}
        lockedTip={lockedTip}
        error={error}
        warning={warning}
        value={value}
        title={value}
        qaId={qaId}
        onClick={handleShowCalendar}
        onKeyDown={handleInputKeyDown}
        actions={actionIcons}
        required={required}
        variant={variant}
        tabIndex={tabIndex}
        {...inputFieldSetProps}
      />
      {showCalendar &&
        !disabled &&
        (portalRef?.current ? ReactDOM.createPortal(CalendarDropdown, portalRef.current) : CalendarDropdown)}
    </div>
  );
};
